import React, { useState, useRef } from "react";
import "./SigninScreen.css";
import placeholderGif from "../../assets/gif/sign_in-animation.gif";
import SigninOTPView from "./Views/SigninOTPView";
import ROUTE_PATHS from "../../utils/routePaths";
import { useNavigate } from "react-router-dom";
import useWSMApi from "services/api/useWSMApi";
import { useMutation } from "@tanstack/react-query";
import SigninEmailView from "./Views/SigninEmailView";
import SnackbarAlert from "components/elements/SnackbarAlert";
import { USER_TYPE } from "utils/constants";
import { STORAGE_KEY } from "utils/strings";
import { trackLogin } from "utils/analytics";

const RESEND_DELAY_SECONDS = 120;

const SigninScreen: React.FC = () => {
  const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string>("");
  const [resendTimer, setResendTimer] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { sendLoginOTP, validateLoginOTP } = useWSMApi();

  const sendOTPMutation = useMutation({
    mutationFn: () =>
      sendLoginOTP({ email: userEmail, type: USER_TYPE }),
    onSuccess: () => {
      setIsOTPSent(true);
      startResendTimer();
    },
  });

  const validateOTPMutation = useMutation({
    mutationFn: () =>
      validateLoginOTP({
        email: userEmail,
        otp: otp,
        type: USER_TYPE,
      }),
    onSuccess: (data) => {
      localStorage.setItem(
        STORAGE_KEY.API_ACCESS_TOKEN_DATA,
        data?.accessToken,
      );
      trackLogin();
      navigate(ROUTE_PATHS.WSM_INITIAL_LOAD, { replace: true });
    },
  });

  const startResendTimer = () => {
    setResendTimer(RESEND_DELAY_SECONDS);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTPClick = (): void => {
    sendOTPMutation.mutate();
  };

  const handleResendOTPClick = (): void => {
    if (resendTimer === 0) {
      sendOTPMutation.mutate();
      startResendTimer();
    }
  };

  const handleChangeEmailClick = (): void => {
    setIsOTPSent(false);
    setResendTimer(0);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleVerifyOTPClick = (): void => {
    validateOTPMutation.mutate();
  };

  const handleOtpChange = (value: string): void => {
    setOtp(value);
  };

  return (
    <div className="wms-mobile-container">
      {sendOTPMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {sendOTPMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {validateOTPMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {validateOTPMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <div className="signin-screen-main-container">
        <img
          src={placeholderGif}
          className="signin-screen-app-logo"
          alt="logo"
        />
        <div>
          <div className="signin-screen-text-container">
            <span>WELCOME TO </span>
            <span style={{ color: "#C2B58C" }}>SABEHUB ECOSYSTEM</span>
          </div>
          {isOTPSent ? (
            <div>
              <div className="signin-screen-otp-send-text ">
                OTP sent to your Email ID successfully.
              </div>
              <SigninOTPView
                userEmail={userEmail}
                onClickVerify={handleVerifyOTPClick}
                onChange={handleOtpChange}
                onClickChangeEmail={handleChangeEmailClick}
                isLoading={validateOTPMutation?.isPending}
                resendTimer={resendTimer}
                onClickResendOTP={handleResendOTPClick}
              />
            </div>
          ) : (
            <SigninEmailView
              isLoading={sendOTPMutation?.isPending}
              userEmail={userEmail}
              setUserEmail={setUserEmail}
              onClickSendOTP={handleSendOTPClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SigninScreen;
